import { useEffect, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Star, ChatTeardropDots, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

const StarPicker = ({ value, onChange, testId }) => (
  <div className="flex gap-1" data-testid={testId}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className={`p-1 transition-transform hover:scale-110 ${
          value >= n ? "text-amber-400" : "text-slate-300"
        }`}
        aria-label={`${n} star${n > 1 ? "s" : ""}`}
      >
        <Star size={26} weight={value >= n ? "fill" : "regular"} />
      </button>
    ))}
  </div>
);

/**
 * CourseFeedbackForm — fires after a course is completed (or shown manually).
 * Counts toward 10% of the transcript score. If unfilled, transcript marks "Feedback pending".
 */
export default function CourseFeedbackForm({ open, onClose, courseId, courseTitle, onSubmitted }) {
  const [form, setForm] = useState({
    overall_rating: 0,
    content_quality: 0,
    instructor_rating: 0,
    pace_rating: 3,
    most_valuable: "",
    improvements: "",
    would_recommend: true,
    additional_comments: "",
  });
  const [saving, setSaving] = useState(false);
  // Round 36 — admin-authored questions override the hardcoded set.
  const [customQuestions, setCustomQuestions] = useState([]);
  const [responses, setResponses] = useState({});
  // Round 38 — release-schedule gating (staff and course-completed students bypass).
  const [availability, setAvailability] = useState(null); // {available, reason, next_release_iso, schedule}

  useEffect(() => {
    if (!open) return;
    api.get(`/courses/${courseId}/feedback/me`)
      .then((r) => {
        if (r.data?.submitted && r.data.feedback) {
          setForm((prev) => ({ ...prev, ...r.data.feedback }));
          setResponses(r.data.feedback.responses || {});
        }
      }).catch(() => {});
    api.get(`/courses/${courseId}/feedback-questions`)
      .then((r) => setCustomQuestions(r.data || []))
      .catch(() => setCustomQuestions([]));
    // Ask the backend whether the release window is open for this student.
    const tz = -new Date().getTimezoneOffset(); // send WAT=+60, EST=-300 (server flips sign)
    api.get(`/courses/${courseId}/feedback/availability`, {
      headers: { "X-Tz-Offset-Minutes": String(new Date().getTimezoneOffset()) },
      params: { tz_offset_minutes: new Date().getTimezoneOffset() },
    }).then(r => setAvailability(r.data))
      .catch(() => setAvailability({ available: true, reason: "unknown" }));
    void tz;
  }, [open, courseId]);

  const handleSubmit = async () => {
    if (!form.overall_rating) {
      toast.error("Please give an overall rating before submitting.");
      return;
    }
    setSaving(true);
    try {
      await api.post(
        `/courses/${courseId}/feedback`,
        { ...form, responses, tz_offset_minutes: new Date().getTimezoneOffset() },
        { headers: { "X-Tz-Offset-Minutes": String(new Date().getTimezoneOffset()) } },
      );
      toast.success("Thank you for your feedback! It now counts toward your transcript.");
      onSubmitted?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit feedback");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-none sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="course-feedback-dialog">
        <DialogHeader>
          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-2">
            <ChatTeardropDots size={26} className="text-blue-800" weight="fill" />
          </div>
          <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Course Feedback</DialogTitle>
          <DialogDescription>
            Help us improve <strong>{courseTitle}</strong>. Your feedback contributes 10% to your final transcript score.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-3">
          {/* Round 38 — release-schedule notice. Students whose window isn't
              open yet see a Clock banner but the form still stays visible so
              admins/completed students can submit. */}
          {availability && !availability.available && (
            <div
              className="border border-amber-300 bg-amber-50 p-3 flex items-start gap-2"
              data-testid="feedback-locked-banner"
            >
              <Clock size={16} className="text-amber-600 mt-0.5 shrink-0" weight="fill" />
              <div className="text-xs text-amber-900">
                <p className="font-semibold">
                  Feedback opens {availability.weekday_name || "this week"} at{" "}
                  {String(availability.schedule?.hour || 13).padStart(2, "0")}:
                  {String(availability.schedule?.minute || 0).padStart(2, "0")}{" "}
                  (your local time).
                </p>
                {availability.next_release_iso && (
                  <p className="mt-0.5" data-testid="feedback-next-release">
                    Next release:{" "}
                    {new Date(availability.next_release_iso).toLocaleString(undefined, {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </p>
                )}
                <p className="mt-0.5 text-amber-800">
                  Finish every other completion step to submit early — course-completed students bypass the schedule.
                </p>
              </div>
            </div>
          )}
          {/* Round 36 — admin-authored custom questions render BEFORE the
              hardcoded defaults so course-specific feedback comes first. */}
          {customQuestions.length > 0 && (
            <div className="space-y-4 pb-2 border-b border-slate-100" data-testid="feedback-custom-questions">
              {customQuestions.map((q) => (
                <div key={q.id}>
                  <Label className="text-sm">{q.question_text}</Label>
                  {q.type === "rating" && (
                    <StarPicker value={responses[q.id] || 0} onChange={(v) => setResponses({...responses, [q.id]: v})} testId={`feedback-custom-${q.id}`} />
                  )}
                  {q.type === "text" && (
                    <Textarea rows={2} value={responses[q.id] || ""} onChange={(e) => setResponses({...responses, [q.id]: e.target.value})} className="rounded-none mt-1" data-testid={`feedback-custom-${q.id}`} />
                  )}
                  {q.type === "yes_no" && (
                    <div className="flex gap-3 mt-2">
                      {["Yes","No"].map(opt => (
                        <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input type="radio" name={`q-${q.id}`} value={opt} checked={responses[q.id] === opt} onChange={() => setResponses({...responses, [q.id]: opt})} data-testid={`feedback-custom-${q.id}-${opt.toLowerCase()}`} />
                          {opt}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div>
            <Label className="text-sm">Overall rating <span className="text-red-500">*</span></Label>
            <StarPicker
              value={form.overall_rating}
              onChange={(v) => setForm({ ...form, overall_rating: v })}
              testId="feedback-overall"
            />
          </div>

          <div>
            <Label className="text-sm">Content quality</Label>
            <StarPicker
              value={form.content_quality}
              onChange={(v) => setForm({ ...form, content_quality: v })}
              testId="feedback-content"
            />
          </div>

          <div>
            <Label className="text-sm">Instructor / facilitator</Label>
            <StarPicker
              value={form.instructor_rating}
              onChange={(v) => setForm({ ...form, instructor_rating: v })}
              testId="feedback-instructor"
            />
          </div>

          <div>
            <Label className="text-sm">Pace of the course</Label>
            <RadioGroup
              value={String(form.pace_rating)}
              onValueChange={(v) => setForm({ ...form, pace_rating: parseInt(v) })}
              className="flex flex-wrap gap-3 mt-1"
            >
              {[
                { v: 1, lbl: "Too slow" },
                { v: 2, lbl: "A bit slow" },
                { v: 3, lbl: "Just right" },
                { v: 4, lbl: "A bit fast" },
                { v: 5, lbl: "Too fast" },
              ].map((opt) => (
                <div key={opt.v} className="flex items-center gap-1.5">
                  <RadioGroupItem value={String(opt.v)} id={`pace-${opt.v}`} data-testid={`feedback-pace-${opt.v}`} />
                  <Label htmlFor={`pace-${opt.v}`} className="text-xs text-slate-700 cursor-pointer">{opt.lbl}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div>
            <Label className="text-sm">What was most valuable about this course?</Label>
            <Textarea
              value={form.most_valuable}
              onChange={(e) => setForm({ ...form, most_valuable: e.target.value })}
              rows={2}
              className="mt-1 rounded-none"
              placeholder="e.g. Real-world case studies on Lagos schools…"
              data-testid="feedback-most-valuable"
            />
          </div>

          <div>
            <Label className="text-sm">What would you like improved?</Label>
            <Textarea
              value={form.improvements}
              onChange={(e) => setForm({ ...form, improvements: e.target.value })}
              rows={2}
              className="mt-1 rounded-none"
              placeholder="e.g. More live Q&A, clearer rubrics…"
              data-testid="feedback-improvements"
            />
          </div>

          <div>
            <Label className="text-sm">Would you recommend this course?</Label>
            <RadioGroup
              value={form.would_recommend ? "yes" : "no"}
              onValueChange={(v) => setForm({ ...form, would_recommend: v === "yes" })}
              className="flex gap-4 mt-1"
            >
              <div className="flex items-center gap-1.5">
                <RadioGroupItem value="yes" id="rec-yes" data-testid="feedback-recommend-yes" />
                <Label htmlFor="rec-yes" className="text-xs text-slate-700 cursor-pointer">Yes</Label>
              </div>
              <div className="flex items-center gap-1.5">
                <RadioGroupItem value="no" id="rec-no" data-testid="feedback-recommend-no" />
                <Label htmlFor="rec-no" className="text-xs text-slate-700 cursor-pointer">No</Label>
              </div>
            </RadioGroup>
          </div>

          <div>
            <Label className="text-sm">Anything else? (optional)</Label>
            <Textarea
              value={form.additional_comments}
              onChange={(e) => setForm({ ...form, additional_comments: e.target.value })}
              rows={2}
              className="mt-1 rounded-none"
              data-testid="feedback-additional"
            />
          </div>

          <Button
            onClick={handleSubmit}
            disabled={saving}
            className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none"
            data-testid="feedback-submit-btn"
          >
            {saving ? "Submitting…" : "Submit Feedback"}
          </Button>
          <p className="text-xs text-slate-400 text-center">Required: overall rating</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
